import { ReferralSettingsPage } from '../features/marketing/ReferralSettingsPage';
import { DiscoveryChipsPage } from '../features/system/DiscoveryChipsPage';
import { CreatorMonitorPage } from '../features/creators/CreatorMonitorPage';
import { CampaignEntriesPage } from '../features/marketing/CampaignEntriesPage';
import { MarketingIndexPage, ReviewToEarnPage } from '../features/mkt/ReviewToEarnPage';
import { UserOutreachPage } from '../features/marketing/UserOutreachPage';
import { FeaturedPage } from '../features/marketing/FeaturedPage';
import { PublicCampaignPage } from '../features/campaigns/PublicCampaignPage';
import { PlaceTaxonomyPage } from '../features/admin/PlaceTaxonomyPage';
import { SocialBonusPage } from '../features/admin/SocialBonusPage';
import { SafariAppInvite } from '../features/landing/StoreButtons';
import { ContactPage } from '../features/contact/ContactPage';
import { NegativeListPage } from '../features/marketing/NegativeListPage';
import { CreatorCouponsPage } from '../features/marketing/CreatorCouponsPage';
import { WebAuthHandoffPage } from '../features/auth/WebAuthHandoffPage';
import { MerchantLandingPage } from '../features/merchant/MerchantLandingPage';
import { MerchantDashboardPage, MerchantToolsGuard } from '../features/merchant/MerchantDashboardPage';
import { ForMerchantPage } from '../features/merchant/ForMerchantPage';
import { MerchantClaimPage } from '../features/merchant/MerchantClaimPage';
import { ContentPage } from '../features/content/ContentPage';
import { PasswordlessLoginPage as LoginPage, PasswordlessLoginPage as RegisterPage, ProfileOnboardingPage, AccountIdentitiesPage } from '../features/auth/PasswordlessPages';
import { useEffect } from 'react';
import { useLocation, navigate, Link } from './router';
import { useAuth } from '../auth/AuthContext';
import { LandingPage } from '../features/landing/LandingPage';
import { AppRedirectPage, ForgotPasswordPage, StaffLoginPage, ResetPasswordPage, VerifyOtpPage } from '../features/auth/AuthPages';
import { ResourcePage, creatorMonitorConfig, creatorVerificationConfig, merchantMonitorConfig, merchantVerificationConfig } from '../features/admin/ResourcePage';
import { campaignCreditConfig, campaignManagementConfig, campaignPostConfig, depositConfig, redemptionConfig } from '../features/admin/configs';
import { KpiPage } from '../features/kpi/KpiPage';
import { MerchantCampaignDetailPage, MerchantCollectionPage } from '../features/merchant/MerchantPages';
import { MerchantPlaceEditPage } from '../features/merchant/MerchantPlaceEditPage';
import { MerchantCampaignsPage } from '../features/merchant/MerchantCampaignsPage';
import { MerchantCampaignCreatePage } from '../features/merchant/MerchantCampaignCreatePage';
import { Logo, Spinner } from '../shared/components';
import { AdminCampaignEditor } from '../features/admin/AdminCampaignEditor';
import { PrivacyPage } from '../features/legal/PrivacyPage';
import { AboutPage } from '../features/legal/AboutPage';
import { WalletPage } from '../features/wallet/WalletPages';
import { PointExchangeRatePage } from '../features/wallet/PointExchangeRatePage';
import { PublicFooter, PublicHeader, usePublicLocale } from '../layouts/PublicFrame';
import { SearchPerformancePage } from '../features/system/SearchPerformancePage';
import { PlacePerformancePage } from '../features/system/PlacePerformancePage';
import { DeliveryTransactionsPage } from '../features/system/DeliveryTransactionsPage';
import { SystemTestingPage } from '../features/system/SystemTestingPage';
import { CampaignPackagesPage } from '../features/system/CampaignPackagesPage';
import { MerchantPitchPresentationPage } from '../features/presentation/MerchantPitchPresentationPage';
import { AdminAccountsPage } from '../features/admin/AdminAccountsPage';
import { canOpenAdminPath } from '../auth/adminAccess';
import { MerchantCouponsPage } from '../features/marketing/MerchantCouponsPage';
import { MerchantApproachesPage } from '../features/marketing/MerchantApproachesPage';
import { PlaceProvincesPage } from '../features/admin/PlaceProvincesPage';
import { PlaceNeighborhoodsPage } from '../features/admin/PlaceNeighborhoodsPage';

function Guard({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const allowed = auth.isAdmin && canOpenAdminPath(auth.session, location.pathname);
  useEffect(() => { if (!auth.restoring && !auth.session) navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, true); else if (!auth.restoring && !allowed) navigate('/forbidden', true); }, [auth.restoring, auth.session, allowed]);
  if (auth.restoring) return <main className="boot"><Logo /><Spinner label="กำลังกู้คืน Session" /></main>;
  if (!auth.session || !allowed) return null;
  return <>{children}</>;
}

function AccountSecurityRedirect() {
  const auth = useAuth();
  useEffect(() => {
    if (auth.restoring) return;
    navigate(auth.session ? (auth.session.needs_profile ? '/onboarding' : '/') : '/login', true);
  }, [auth.restoring, auth.session]);
  return null;
}

function StaticPage({ type }: { type: 'privacy' | 'terms' | 'forbidden' | 'not-found' }) {
  const copy = {
    privacy: ['Privacy', 'Beaw ใช้ข้อมูลเท่าที่จำเป็นเพื่อให้บริการ โดยหน้า landing ไม่ส่งตำแหน่งแบบละเอียดหรือข้อความรีวิวเข้าสู่ analytics'],
    terms: ['Terms', 'การใช้ campaign และ reward เป็นไปตามเกณฑ์ qualification, onsite verification และนโยบายของ Beaw'],
    forbidden: ['ไม่มีสิทธิ์เข้าถึง', 'พื้นที่นี้สำหรับบัญชี Admin เท่านั้น'],
    'not-found': ['ไม่พบหน้านี้', 'หน้านี้อาจถูกย้ายหรือลิงก์ไม่ถูกต้อง'],
  }[type];
  if (type === 'terms') return <PublicStaticPage copy={copy} />;
  return <main className="static-page"><Link to="/"><Logo /></Link><section><p className="eyebrow">BEAW.CLUB</p><h1>{copy[0]}</h1><p>{copy[1]}</p><Link to="/" className="button button--primary">กลับหน้าแรก</Link></section></main>;
}

function PublicStaticPage({ copy }: { copy: string[] }) {
  const { locale, changeLocale } = usePublicLocale();
  return <div className="public-static"><PublicHeader locale={locale} onLocaleChange={changeLocale} /><main><section><p className="eyebrow">BEAW.CLUB</p><h1>{copy[0]}</h1><p>{copy[1]}</p><Link to="/" className="button button--primary">กลับหน้าแรก</Link></section></main><PublicFooter /></div>;
}

export function App() {
  const { pathname } = useLocation();
  const contentMatch = pathname.match(/^\/content\/([^/]+)\/?$/);
  return <><SafariAppInvite contentId={contentMatch ? decodeURIComponent(contentMatch[1]) : undefined} /><AppRoutes /></>;
}

function AppRoutes() {
  const { pathname } = useLocation();
  const auth = useAuth();
  useEffect(() => {
    if (!auth.restoring && auth.session?.needs_profile && !pathname.startsWith('/content/') && pathname !== '/missions' && pathname !== '/missions/' && !['/auth/handoff', '/onboarding', '/terms', '/privacy', '/contact', '/contact/', '/for-merchant', '/for-merchant/', '/for-merchants'].includes(pathname)) navigate(pathname === '/for-merchant/join' ? '/onboarding?next=/for-merchant/join' : `/onboarding${location.search}`, true);
  }, [auth.restoring, auth.session?.needs_profile, pathname]);
  const contentMatch = pathname.match(/^\/content\/([^/]+)\/?$/);
  if (contentMatch) return <ContentPage key={contentMatch[1]} id={decodeURIComponent(contentMatch[1])} />;
  if (pathname === '/missions' || pathname === '/missions/') return <PublicCampaignPage key={location.search} id={new URLSearchParams(location.search).get('campaign_id') || ''} />;
  const bonusMatch = pathname.match(/^\/reviews\/([^/]+)\/evidence$/);
  if (bonusMatch) return auth.restoring ? null : auth.session ? <SocialBonusPage key={bonusMatch[1]} reviewId={decodeURIComponent(bonusMatch[1])} /> : <LoginPage />;
  if (pathname === '/onboarding') return auth.session ? <ProfileOnboardingPage /> : <LoginPage />;
  if (pathname === '/account/security') return new URLSearchParams(location.search).get('verify') === 'phone' ? <AccountIdentitiesPage /> : <AccountSecurityRedirect />;
  if (pathname === '/auth/handoff') return <WebAuthHandoffPage />;
  if ((pathname === '/login' || pathname === '/login/staff') && auth.restoring) return null;
  if ((pathname === '/login' || pathname === '/login/staff') && auth.session) { navigate('/', true); return null; }
  if (pathname === '/login/staff') return <StaffLoginPage />;
  const legacy = new URLSearchParams(window.location.search);
  if (pathname === '/creator/admin' || pathname === '/admin/economy') {
    const view = legacy.get('view'); navigate(view === 'point-exchange-rate' ? '/admin/wallet/exchange-rate' : view === 'creator-verification' ? '/admin/creators/verification' : view === 'review-moderation' ? '/admin/requests/campaign-credits' : '/admin/requests/redemptions', true); return null;
  }
  if (pathname === '/creator/merchant') {
    const section = legacy.get('merchant_section'); navigate(section === 'wallet' ? '/for-merchant/manage' : section === 'places' ? '/for-merchant/manage' : section === 'campaign' ? '/merchant/campaigns' : '/for-merchant/manage', true); return null;
  }
  if (pathname === '/') return <LandingPage />;
  if (pathname === '/mkt' || pathname === '/mkt/') return <MarketingIndexPage />;
  if (pathname === '/mkt/review-to-earn' || pathname === '/mkt/review-to-earn/') return <ReviewToEarnPage />;
  if (pathname === '/for-merchant' || pathname === '/for-merchants' || pathname === '/for-merchant/') return <MerchantLandingPage />;
  if (pathname === '/for-merchant/manage' || pathname === '/merchant/wallet') return <MerchantDashboardPage />;
  if (pathname === '/for-merchant/join') return <ForMerchantPage />;
  if (pathname === '/for-merchant/claim') return <MerchantClaimPage />;
  if (pathname === '/login') return <LoginPage />;
  if (pathname === '/login/app-redirect') return <AppRedirectPage />;
  if (pathname === '/register') return <RegisterPage />;
  if (pathname === '/verify-otp') return <VerifyOtpPage />;
  if (pathname === '/forgot-password') return <ForgotPasswordPage />;
  if (pathname === '/reset-password') return <ResetPasswordPage />;
  if (pathname === '/contact' || pathname === '/contact/') return <ContactPage />;
  if (pathname === '/privacy') return <PrivacyPage />;
  if (pathname === '/for-creator' || pathname === '/about') return <AboutPage />;
  if (pathname === '/terms' || pathname === '/forbidden') return <StaticPage type={pathname.slice(1) as 'terms' | 'forbidden'} />;
  if (pathname === '/presentation' || pathname === '/presentationslide') return <Guard><MerchantPitchPresentationPage /></Guard>;
  const adminRoutes: Record<string, React.ReactNode> = {
    '/admin/system/admins': <AdminAccountsPage />,
    '/admin/creators/verification': <ResourcePage config={creatorVerificationConfig} />,
    '/admin/creators/monitor': <CreatorMonitorPage />,
    '/admin/creators/monitor/': <CreatorMonitorPage />,
    '/admin/merchants/verification': <ResourcePage config={merchantVerificationConfig} />,
    '/admin/merchants/monitor': <ResourcePage config={merchantMonitorConfig} />,
    '/admin/places': <PlaceProvincesPage />,
    '/admin/places/taxonomy': <PlaceTaxonomyPage />,
    '/admin/places/neighborhoods': <PlaceNeighborhoodsPage />,
    '/admin/requests/redemptions': <ResourcePage config={redemptionConfig} />,
    '/admin/requests/deposits': <ResourcePage config={depositConfig} />,
    '/admin/requests/campaign-posts': <ResourcePage config={campaignPostConfig} />,
    '/admin/requests/campaign-credits': <ResourcePage config={campaignCreditConfig} />,
    '/admin/system/kpi': <KpiPage />,
    '/admin/system/place-performance': <PlacePerformancePage />,
    '/admin/system/search-performance': <SearchPerformancePage />,
    '/admin/system/transactions': <DeliveryTransactionsPage />,
    '/admin/system/testing': <SystemTestingPage />,
    '/admin/system/discovery-chips': <DiscoveryChipsPage />,
    '/admin/system/campaign-packages': <CampaignPackagesPage />,
    '/admin/marketing/merchant-coupons': <MerchantCouponsPage />,
    '/admin/marketing/creator-coupons': <CreatorCouponsPage />,
    '/admin/marketing/review-to-earn': <CampaignEntriesPage />,
    '/admin/marketing/featured': <FeaturedPage />,
    '/admin/marketing/referral': <ReferralSettingsPage />,
    '/admin/marketing/messages': <UserOutreachPage />,
    '/admin/marketing/negative-list': <NegativeListPage />,
    '/admin/marketing/merchant-approaches': <MerchantApproachesPage />,
    '/admin/wallet/overview': <WalletPage kind="overview" />,
    '/admin/wallet/exchange-rate': <PointExchangeRatePage />,
    '/admin/wallet/campaigns': <WalletPage kind="campaign-wallets" />,
    '/admin/wallet/ledger': <WalletPage kind="ledger" />,
    '/admin/wallet/cash-movements': <WalletPage kind="cash-movements" />,
    '/admin/wallet/reconciliation': <WalletPage kind="reconciliation" />,
    '/admin/campaigns': <ResourcePage config={campaignManagementConfig} />,
    '/admin/campaigns/new': <AdminCampaignEditor />,
  };
  if (adminRoutes[pathname]) return <Guard>{adminRoutes[pathname]}</Guard>;
  const dynamicAdmin = [
    ['/admin/creators/verification/', creatorVerificationConfig], ['/admin/creators/monitor/', creatorMonitorConfig], ['/admin/merchants/verification/', merchantVerificationConfig], ['/admin/merchants/monitor/', merchantMonitorConfig], ['/admin/requests/redemptions/', redemptionConfig], ['/admin/requests/deposits/', depositConfig], ['/admin/requests/campaign-posts/', campaignPostConfig], ['/admin/requests/campaign-credits/', campaignCreditConfig], ['/admin/campaigns/', campaignManagementConfig],
  ] as const;
  const adminCampaignEdit = pathname.match(/^\/admin\/campaigns\/([^/]+)\/edit$/);
  if (adminCampaignEdit) return <Guard><AdminCampaignEditor campaignId={decodeURIComponent(adminCampaignEdit[1])} /></Guard>;
  const dynamicAdminMatch = dynamicAdmin.find(([prefix]) => pathname.startsWith(prefix));
  if (dynamicAdminMatch) {
    const [prefix, config] = dynamicAdminMatch;
    const initialId = decodeURIComponent(pathname.slice(prefix.length).split('/')[0] || '');
    return <Guard><ResourcePage config={config} initialId={initialId} /></Guard>;
  }
  const merchantRoutes: Record<string, React.ReactNode> = {
    '/merchant/verification': <MerchantDashboardPage />, '/merchant/verify': <MerchantDashboardPage />,
    '/merchant/places': <MerchantCollectionPage type="places" />,
    '/merchant/campaigns': <MerchantCampaignsPage />, '/merchant/campaigns/new': <MerchantCampaignCreatePage />, '/merchant/campaign/create': <MerchantCampaignCreatePage />,
    '/merchant/reviews': <MerchantCollectionPage type="reviews" />,
  };
  if (['/merchant/verification', '/merchant/verify', '/merchant/places'].includes(pathname)) return <MerchantDashboardPage />;
  if (merchantRoutes[pathname]) return <MerchantToolsGuard>{merchantRoutes[pathname]}</MerchantToolsGuard>;
  const placeEdit = pathname.match(/^\/merchant\/places\/([^/]+)\/edit$/);
  if (placeEdit) return <MerchantToolsGuard><MerchantPlaceEditPage placeId={decodeURIComponent(placeEdit[1])} /></MerchantToolsGuard>;
  const campaignDetail = pathname.match(/^\/merchant\/campaigns\/([^/]+)$/);
  if (campaignDetail) return <MerchantToolsGuard><MerchantCampaignDetailPage campaignId={decodeURIComponent(campaignDetail[1])} /></MerchantToolsGuard>;
  return <StaticPage type="not-found" />;
}
